import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate, Link } from 'react-router-dom';

import {
  AppBar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  CssBaseline,
  Drawer,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListItem,
  Menu,
  MenuItem,
  Popover,
  Stack,
  styled,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';

import SideNavigation from 'app/components/layout/side-navigaion/side-navigation';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  Blog,
  fetchBlogs,
  fetchBlogsByCategory,
  fetchBlogsByCategoryUnauthorized,
  fetchBlogsUnauthorized,
  resetBlogs,
} from 'app/shared/reducers/blogs';
import MenuIcon from '@mui/icons-material/Menu';
import { convertDate, encodedTitle } from 'app/utils/common-utils/common-utils';
import { darkModeToggle, setTheme } from 'app/shared/reducers/dark-mode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeToggleIcon from '../../icons/header-icons/dark-mode-toggle-icon';
import Badge from '@mui/material/Badge';
import SquareChatIcon from 'app/components/icons/header-icons/square-chat-icon';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import useMediaQuery from '@mui/material/useMediaQuery';
import Logout from '@mui/icons-material/Logout';
import AuthorisationModal from 'app/components/common/authorization-modal/authorization-modal';
import './blog-list-view.scss';
import PrimaryHeader from 'app/components/layout/header/primary-header';
import { Storage } from 'react-jhipster';
import AmyIcon from 'app/components/common/cards/chat-card/amy-icons';
import { toggleModalLogin } from 'app/shared/reducers/authentication';
import axios from 'axios';
import { clearSearhedActivities } from 'app/shared/reducers/activity-filter';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { GallerySlide } from '../activity-details-section/activity-details-card/activity-details-card';
import Video from 'yet-another-react-lightbox/plugins/video';
import BlogListSingle from './blog-list-single';
import toast from 'react-hot-toast';

const BlogListView = () => {
  const [isSearchBarVisible, setIsSearchBarVisible] = React.useState(true);

  // Shared lightbox state (one instance for all cards)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState<GallerySlide[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (slides: GallerySlide[], index: number) => {
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const searchResults = useAppSelector(state => state.filters.searchResults);
  const searchLoading = useAppSelector(state => state.filters.loading);
  const searchKeyword = useAppSelector(state => state.filters.searchKeyWord);
  const consentPending = useAppSelector(state => state.authentication.consentPending);

  useEffect(() => {
    const handleResize = () => setIsSearchBarVisible(window.innerWidth >= 900);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadingContainerStyle = { height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Pull paginated blog state from Redux
  const blogData = useAppSelector(state => state.blogs.blogs);
  const loading = useAppSelector(state => state.blogs.loading);
  const loadingMore = useAppSelector(state => state.blogs.loadingMore);
  const hasMore = useAppSelector(state => state.blogs.hasMore);
  const currentPage = useAppSelector(state => state.blogs.currentPage);
  const [error, setError] = useState(false);

  const [toggleMapValue, setToggleMapValue] = useState(false);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // IntersectionObserver sentinel ref for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  const loadPage = useCallback(
    async (page: number) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        if (isAuthenticated) {
          await dispatch(fetchBlogs(page));
        } else {
          await dispatch(fetchBlogsUnauthorized(page));
        }
      } catch {
        setError(true);
      } finally {
        isFetchingRef.current = false;
      }
    },
    [dispatch, isAuthenticated, consentPending]
  );

  // Initial load
  useEffect(() => {
    dispatch(resetBlogs());
    dispatch(clearSearhedActivities());
    loadPage(0);
    return () => {
      dispatch(clearSearhedActivities());
    };
  }, []);

  // Infinite scroll: observe sentinel div relative to the scroll container.
  // root must be the scroll container (not the viewport) because the consent-pending
  // banner pushes the container below the viewport fold on typical screen heights,
  // making the sentinel unreachable by a viewport-based observer.
  useEffect(() => {
    if (!sentinelRef.current || !scrollContainerRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadPage(currentPage + 1);
        }
      },
      { threshold: 0.1, root: scrollContainerRef.current }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, currentPage, loadPage]);

  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);

  const onhandleAccordionClick = async (categoryId: number, currentState: any) => {
    setCategory(categoryId);
    if (!currentState) {
      try {
        if (isAuthenticated) {
          await dispatch(fetchBlogsByCategory({ categoryId, subCategoryId: null }));
        } else {
          await dispatch(fetchBlogsByCategoryUnauthorized({ categoryId, subCategoryId: null }));
        }
      } catch {
        setError(true);
      }
    } else {
      dispatch(resetBlogs());
      loadPage(0);
    }
  };

  const onSubCategoryClick = async (categoryId: number, subCategoryId: number) => {
    setCategory(categoryId);
    setSubCategory(subCategoryId);
    try {
      if (isAuthenticated) {
        await dispatch(fetchBlogsByCategory({ categoryId, subCategoryId }));
      } else {
        await dispatch(fetchBlogsByCategoryUnauthorized({ categoryId, subCategoryId }));
      }
    } catch {
      setError(true);
    }
  };

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawerWidth = 315;
  const drawerWidthSm = 248;

  const reduxDarkModeValue = useAppSelector(state => state.darkMode.darkMode);
  const [currentComponent, setCurrentComponent] = React.useState(' ');
  const [authModalOpen, setAuthModalOpen] = React.useState(false);

  const toggleThemeHandler = () => {
    dispatch(darkModeToggle(!reduxDarkModeValue));
    Storage.local.set('dark-mode', !reduxDarkModeValue);
    if (isAuthenticated) dispatch(setTheme({ darkMode: !reduxDarkModeValue }));
  };

  const handleAuthModalOpen = () => {
    setAuthModalOpen(true);
    dispatch(toggleModalLogin());
  };
  const handleAuthModalClose = () => setAuthModalOpen(false);

  const handleLogin = () => {
    setCurrentComponent('login');
    handleAuthModalOpen();
    handleMenuClose();
  };

  const handleLogout = () => {
    setCurrentComponent('logout');
    handleAuthModalOpen();
    handleMenuClose();
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const menuId = 'primary-search-menu';

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileMoreAnchorEl(null);
  };
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setMobileMoreAnchorEl(event.currentTarget);

  const renderMenu = (
    <Menu
      sx={{ top: '37px' }}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '& .MuiAvatar-root': { width: 32, height: 32, ml: -0.5, mr: 1 },
          '&:before': {
            content: '""', display: 'block', position: 'absolute', top: 0, right: 14,
            width: 10, height: 10, bgcolor: 'background.paper', transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
          },
        },
      }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {isAuthenticated ? (
        <MenuItem onClick={handleLogout}><ListItemIcon><Logout /></ListItemIcon>Logout</MenuItem>
      ) : (
        <MenuItem onClick={handleLogin}><ListItemIcon><LoginIcon /></ListItemIcon>Login</MenuItem>
      )}
    </Menu>
  );

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id="menu-mobile"
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMobileMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem>
        <IconButton onClick={toggleThemeHandler} size="large" aria-label="dark-mode-toggler" color="inherit">
          <Badge>{theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeToggleIcon />}</Badge>
        </IconButton>
      </MenuItem>
      <MenuItem>
        <IconButton size="large" aria-label="chat" color="inherit">
          <Badge>{theme.palette.mode === 'dark' ? <QuestionAnswerRoundedIcon /> : <SquareChatIcon />}</Badge>
        </IconButton>
      </MenuItem>
      {isAuthenticated ? (
        <MenuItem onClick={handleLogout}><IconButton size="large" color="inherit"><LogoutIcon /></IconButton></MenuItem>
      ) : (
        <MenuItem onClick={handleLogin}><IconButton size="large" color="inherit"><LoginIcon /></IconButton></MenuItem>
      )}
    </Menu>
  );

  const DrawerHeader = styled('div')(({ theme: t }) => ({
    display: 'flex', alignItems: 'center', padding: t.spacing(0, 1),
    ...t.mixins.toolbar, justifyContent: 'flex-end',
  }));

  const drawer = (
    <Box
      sx={{
        border: 'none', maxHeight: '100vh', overflowY: 'scroll', overflowX: 'hidden',
        background: isDarkMode
          ? '#1F1F26'
          : 'transparent linear-gradient(182deg, #F7F5EF 0%, #F2F2FA 49%, #f1f1f8 100%) 0% 0% no-repeat padding-box;',
        maxWidth: { sm: drawerWidthSm, lg: drawerWidth, md: drawerWidthSm },
        backdropFilter: 'blur(10px)', minHeight: '100%',
        '&::-webkit-scrollbar': { display: 'none' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'secondary.contrastText', height: '10px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      }}
    >
      {consentPending && <Box sx={{ height: '44px', flexShrink: 0 }} />}
      <DrawerHeader sx={{ justifyContent: 'center', height: '79px', border: 'none' }}>
        <Link to={'/'} style={{ paddingLeft: '0px', paddingRight: '50px' }}>
          <AmyIcon sx={{ width: '159px', height: '100px' }} />
        </Link>
      </DrawerHeader>
      <SideNavigation
        category={category}
        subCategory={subCategory}
        onSubCategoryClick={onSubCategoryClick}
        onhandleAccordionClick={onhandleAccordionClick}
        onButtonClicked={() => {}}
        onMapClicked={() => {}}
        toggleMapValue={toggleMapValue}
        setToggleMapValue={setToggleMapValue}
        onFeatureClicked={() => {}}
      />
    </Box>
  );

  useEffect(() => {
    const filteredSearchResults = searchResults?.filter(blog => blog.eventType === 'Blog');
    if (filteredSearchResults?.length === 0) localStorage.removeItem('searchKeyword');
  }, [searchResults]);

  const [anchorElMock, setAnchorElMock] = useState<HTMLElement | null>(null);
  const [selectedBlogId, setSelectedBlogId] = useState<number>(null);
  const openModal = Boolean(anchorElMock);
  const menuItemId = openModal ? 'simple-popover' : undefined;

  const handleClickModal = (event: MouseEvent<HTMLElement>, blogId: number) => {
    setSelectedBlogId(blogId);
    setAnchorElMock(event.currentTarget);
  };
  const handleCloseModal = () => setAnchorElMock(null);

  const handleFeatureDirectly = async (blogId: number) => {
    if (consentPending) { toast.error('Parental consent is required before you can feature blogs.'); return; }
    try {
      const response = await axios.post(`/api/payment-service/feature-event/unlock-free?eventType=4&eventId=${blogId}`);
      if (response.data?.status === 'success') {
        toast.success('Blog marked as Featured');
      } else {
        toast.error(response.data?.message || 'Failed to feature blog');
      }
    } catch (err) {
      toast.error('Something went wrong');
    }
  };

  const renderBlogCard = (blog: Blog, index: number) => (
    <div key={`blog-${blog?.id}`} className="mb-3 mt-3" style={{ display: 'flex', flexDirection: 'column' }}>
      <Card
        sx={{ width: '100%', borderRadius: '15px', cursor: 'pointer', position: 'relative' }}
      >
        {blog?.featured && (
          <div style={{
            position: 'absolute', top: 0, left: 0, background: '#674BFF', color: '#fff',
            padding: '5px 10px', borderRadius: '0 0 15px 0', zIndex: 999, fontSize: '12px', fontWeight: 'bold',
          }}>
            Featured
          </div>
        )}

        <BlogListSingle data={blog} isFirst={index === 0} onLightboxOpen={openLightbox} />

        <CardContent component="div" sx={{ backgroundColor: 'secondary.dark' }}>
          <div className="d-flex justify-content-between">
            <Typography
              gutterBottom variant="h5" component="div"
              sx={{ color: theme.palette.secondary.main }}
              onClick={() => navigate(`/blog-single/${encodedTitle(blog?.blogTitle)}-${blog.id}`)}
            >
              {blog?.blogTitle}
            </Typography>
            <span onClick={event => handleClickModal(event, blog.id)}>
              <MoreHorizOutlinedIcon aria-describedby={menuItemId} sx={{ cursor: 'pointer', fontSize: '19px', color: '#676E7B' }} />
            </span>
          </div>

          <Typography
            variant="body2" color="text.secondary" component="div"
            sx={{
              color: theme.palette.secondary.light,
              display: '-webkit-box', WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis',
              maxHeight: '6em', lineClamp: 3, WebkitLineClamp: 3,
            }}
            onClick={() => navigate(`/blog-single/${encodedTitle(blog?.blogTitle)}-${blog.id}`)}
          >
            {blog?.postDescription && <div dangerouslySetInnerHTML={{ __html: blog.postDescription }} />}
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', backgroundColor: 'secondary.dark' }}>
          <div>
            <Button variant="text" sx={{ color: theme.palette.secondary.light, textTransform: 'capitalize' }}>
              {convertDate(blog?.postedOn)}
            </Button>
            {isAuthenticated && selectedBlogId === blog?.id && (
              <Popover
                id={menuItemId}
                open={openModal && selectedBlogId === blog?.id}
                anchorEl={anchorElMock}
                onClose={handleCloseModal}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <ListItem sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', alignItems: 'inherit', padding: '8px 16px' }}>
                  <button
                    style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => handleFeatureDirectly(blog.id)}
                  >
                    <ListItemText className="text-start">
                      <Typography sx={{ p: 0, fontSize: '14px', cursor: 'pointer', color: isDarkMode ? '#fff' : '#000' }}>
                        Feature blog
                      </Typography>
                    </ListItemText>
                  </button>
                </ListItem>
              </Popover>
            )}
          </div>
          <Button variant="text" sx={{ color: theme.palette.secondary.light, textTransform: 'capitalize' }}>
            by {blog?.authorName}
          </Button>
        </CardActions>
      </Card>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden' }}>
      <CssBaseline />

      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          top: consentPending ? '44px' : 0,
          width: { sm: `calc(100% - ${drawerWidthSm}px)`, md: `calc(100% - ${drawerWidthSm}px)`, lg: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidthSm}px`, md: `${drawerWidthSm}px`, lg: `${drawerWidth}px` },
          background: isDarkMode ? '#1F1F26' : 'none',
        }}
      >
        <Toolbar
          className="amy-top-nav"
          sx={{
            backgroundColor: 'transparent linear-gradient(118deg, #F7F5EF 0%, #F2F2FA 49%, #EFEFF5 100%) 0% 0% no-repeat',
            border: 'none',
            backdropFilter: 'blur(10px)',
          }}
        >
          <IconButton
            color="inherit" aria-label="open drawer" edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, top: '10px' }}
          >
            <MenuIcon />
          </IconButton>
          <PrimaryHeader />
        </Toolbar>
      </AppBar>

      {renderMobileMenu}
      {renderMenu}

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidthSm, md: drawerWidthSm, lg: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthSm } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: { sm: drawerWidthSm, md: drawerWidthSm, lg: drawerWidth } } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1, p: 3, width: '0px',
          background: isDarkMode
            ? '#1F1F26'
            : 'transparent linear-gradient(172deg, #F7F5EF 0%, #F2F2FA 49%, #EFEFF5 100%) 0% 0% no-repeat padding-box;',
          border: 'none',
        }}
      >
        {consentPending && <Box sx={{ height: '44px' }} />}
        <Toolbar />

        <div className="col-sm-12 col-md-10 col-lg-8 col-scroll">
          <div className="col-12" style={{ display: 'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row', justifyContent: 'space-between' }}>
            <Typography variant="h6" noWrap component="div" gutterBottom>
              Read what&apos;s happening
            </Typography>
          </div>

          <div ref={scrollContainerRef} className="col-sm-12 col-md-12 col-lg-12" style={{ height: '77vh', overflow: 'hidden scroll' }}>
            <style>{`::-webkit-scrollbar{width:0;background:transparent}::-webkit-scrollbar-thumb{background:transparent}`}</style>

            {loading ? (
              <Grid item xs={12}>
                <div style={loadingContainerStyle}>Loading...</div>
              </Grid>
            ) : error ? (
              <Grid item xs={12}>
                <div style={loadingContainerStyle}>Error occurred while fetching data.</div>
              </Grid>
            ) : searchResults?.length ? (
              <div>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'center' }}>
                  <Typography color="secondary.light">{`${searchResults?.length} Search results found for  `}</Typography>
                  <Typography color={'secondary.main'} fontWeight={600}>"{`${searchKeyword}`}"</Typography>
                </Stack>
                {searchResults.map((blog: Blog, idx: number) => (
                  <div key={`search-${blog?.id}`} className="mb-3 mt-3" style={{ display: 'flex', flexDirection: 'column' }}>
                    <Card sx={{ width: '100%', borderRadius: '15px', cursor: 'pointer' }}>
                      <Link to={`/blog-single/${encodedTitle(blog?.blogTitle)}-${blog?.id}`} className="text-decoration-none">
                        <div style={{ position: 'relative' }}>
                          <BlogListSingle data={blog} isFirst={idx === 0} onLightboxOpen={openLightbox} />
                          <Button
                            variant="contained" size="small"
                            style={{ position: 'absolute', bottom: 10, left: 10, background: '#F6F7F8', color: '#900095', textTransform: 'capitalize', borderRadius: '16px' }}
                          >
                            {typeof blog?.blogType === 'object' ? blog.blogType.category : blog?.blogType}
                          </Button>
                        </div>
                        <CardContent component="div" sx={{ backgroundColor: 'secondary.dark' }}>
                          <Typography gutterBottom variant="h5" component="div" sx={{ color: theme.palette.secondary.main }}>
                            {encodedTitle(blog?.blogTitle)}
                          </Typography>
                          <Typography
                            variant="body2" color="text.secondary" component="div"
                            sx={{ color: theme.palette.secondary.light, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '6em', lineClamp: 3, WebkitLineClamp: 3 }}
                          >
                            {blog?.postDescription && <div dangerouslySetInnerHTML={{ __html: blog.postDescription }} />}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', backgroundColor: 'secondary.dark' }}>
                          <Button variant="text" sx={{ color: theme.palette.secondary.light, textTransform: 'capitalize' }}>{convertDate(blog?.postedOn)}</Button>
                          <Button variant="text" sx={{ color: theme.palette.secondary.light, textTransform: 'capitalize' }}>by {blog?.authorName}</Button>
                        </CardActions>
                      </Link>
                    </Card>
                  </div>
                ))}
              </div>
            ) : blogData.length > 0 ? (
              <div>
                {blogData.map((blog: Blog, index: number) => renderBlogCard(blog, index))}

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} style={{ height: '1px' }} />

                {loadingMore && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                )}

                {!hasMore && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <Typography variant="body2" color="secondary.light">You've reached the end</Typography>
                  </Box>
                )}
              </div>
            ) : (
              <Grid item xs={12} style={loadingContainerStyle}>
                <Box display="flex" flexDirection="column" alignItems="center" gap="13px">
                  <Box
                    component="img"
                    sx={{ height: 233, width: 350, maxHeight: { xs: 233, md: 167 }, maxWidth: { xs: 350, md: 160 } }}
                    alt="Image"
                    src="content/images/empty.svg"
                  />
                  <span>No blog data available.</span>
                </Box>
              </Grid>
            )}
          </div>
        </div>
      </Box>

      {/* Single shared Lightbox for all blog cards */}
      <Lightbox
        carousel={{ finite: true }}
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        plugins={[Video]}
        index={lightboxIndex}
      />

      <AuthorisationModal
        onClose={handleAuthModalClose}
        authModalOpen={authModalOpen}
        fullScreen={fullScreen}
        handleAuthModalClose={handleAuthModalClose}
        currentComponent={currentComponent}
        setCurrentComponent={setCurrentComponent}
      />
    </Box>
  );
};

export default BlogListView;

